"use client";

import { useEffect, useState, ReactNode } from "react";
import Bugsnag from "@bugsnag/js";
import { PrismicPreview } from "@prismicio/next";
import { PrismicProvider } from "@prismicio/react";
import { useReducedMotion } from "@react-spring/web";
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import axios, { AxiosError } from "axios";
import Link from "next/link";
import type { LinkProps } from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { env } from "next-runtime-env";

import AdminPanel from "components/adminPanel/AdminPanel";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import { setAdminQueryDefaults } from "queries/legacy/admin-queries";
import { setCartQueryDefaults } from "queries/legacy/cart-queries";
import { setHealthQueryDefaults } from "queries/legacy/health-queries";
import { setOrderQueryDefaults } from "queries/legacy/order-queries";
import { setProductQueryDefaults } from "queries/legacy/product-queries";
import { setWebsiteQueryDefaults } from "queries/legacy/website-queries";
import { pageview as gtmPageview } from "tracking/gtm"; // Renamed to avoid conflict
import { repositoryName, useReStylePrismicToolbar } from "utils/prismic-utils";

// GTM Tracking
function ClientGtmTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (pathname) {
      const url = pathname + (searchParams ? searchParams.toString() : "");
      gtmPageview(url);
    }
  }, [pathname, searchParams]);

  return null;
}

// New Client Component for AdminPanel
function ClientAdminPanel() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  if (
    env("NEXT_PUBLIC_CANVAS_ENV") === "development" ||
    env("NEXT_PUBLIC_CANVAS_ENV") === "staging"
  ) {
    return <AdminPanel />;
  }
  return null;
}

// Axios setup (runs once on module import)
axios.defaults.timeout = Number(
  env("NEXT_PUBLIC_REQUEST_DEFAULT_TIMEOUT_MS") ?? 8000
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error instanceof AxiosError &&
      error.code === "ECONNABORTED" &&
      error.message.includes("timeout")
    ) {
      return Promise.reject(
        Error(
          `${
            error.config?.url
              ? `Request to ${error.config?.url}`
              : "Request without config"
          } timed out after ${
            env("NEXT_PUBLIC_REQUEST_DEFAULT_TIMEOUT_MS") || 8000
          }ms`
        )
      );
    }
    return Promise.reject(error);
  }
);

// Bugsnag setup (runs once on module import if conditions met)
const bugsnagAPIKey = env("NEXT_PUBLIC_BUGSNAG_API_KEY");
if (
  typeof window !== "undefined" && // Ensure Bugsnag runs only on client
  env("NEXT_PHASE") !== "phase-production-build" &&
  bugsnagAPIKey &&
  (env("NEXT_PUBLIC_CANVAS_ENV") === "staging" ||
    env("NEXT_PUBLIC_CANVAS_ENV") === "production") &&
  // @ts-ignore
  !Bugsnag._client
) {
  Bugsnag.start({
    apiKey: bugsnagAPIKey,
    appVersion: env("NEXT_BUILD_ID"),
    plugins: [],
  });
}

interface ClientProvidersProps {
  children: ReactNode;
  pageProps?: {
    // pageProps might not be directly available in app router layout in the same way
    dehydratedState?: unknown;
  };
}

// New component to wrap PrismicPreview
function PrismicPreviewWrapper({ children }: { children: ReactNode }) {
  return (
    <PrismicPreview
      exitPreviewURL="/_canvas/api/exit-preview"
      repositoryName={repositoryName}
      updatePreviewURL="/_canvas/api/preview"
    >
      {children}
    </PrismicPreview>
  );
}

export default function ClientProviders({
  children,
  pageProps,
}: ClientProvidersProps) {
  useReducedMotion();
  useReStylePrismicToolbar();

  const [queryClient] = useState(() => {
    const client = new QueryClient();
    setProductQueryDefaults(client);
    setHealthQueryDefaults(client);
    setCartQueryDefaults(client);
    setOrderQueryDefaults(client);
    setWebsiteQueryDefaults(client);
    setAdminQueryDefaults(client);
    return client;
  });

  return (
    <PrismicProvider
      internalLinkComponent={(props: LinkProps) => <Link {...props} />}
    >
      <PrismicPreviewWrapper>
        <QueryClientProvider client={queryClient}>
          <Hydrate state={pageProps?.dehydratedState}>
            <ErrorBoundary>
              <ClientAdminPanel />
              <ClientGtmTracker />
              {children}
            </ErrorBoundary>
            <ReactQueryDevtools initialIsOpen={false} />
          </Hydrate>
        </QueryClientProvider>
      </PrismicPreviewWrapper>
    </PrismicProvider>
  );
}
